import {
  isPriceSubunitCurrency,
  normalizeQuoteCurrencyCode,
} from "./quote-currency";

type PrimitiveValue = number | string | null | undefined;
export type SupportedDisplayCurrency = "USD" | "EUR";

/**
 * Every formatter takes the reader's app locale ("ca", "es"…) and follows its conventions:
 * separators, where the currency symbol goes and how large amounts are abbreviated. Without a
 * locale they fall back to English.
 */
type LocaleOption = { locale?: string | null };

type CurrencyFormatOptions = LocaleOption & {
  includeSign?: boolean;
  currency?: string;
  maximumFractionDigits?: number;
};

/** The regional variant each app language is read in. */
const INTL_LOCALES: Record<string, string> = {
  en: "en-US",
  es: "es-ES",
  ca: "ca-ES",
  de: "de-DE",
  fr: "fr-FR",
  it: "it-IT",
  pt: "pt-PT",
};

export const toIntlLocale = (locale?: string | null): string =>
  INTL_LOCALES[(locale ?? "").split(/[-_]/)[0].toLowerCase()] ?? "en-US";

const formatterCache = new Map<string, Intl.NumberFormat>();

// Spanish, Italian and Portuguese leave four-digit numbers ungrouped by default; financial
// columns read better when 1.234 and 12.345 are grouped alike.
const getFormatter = (locale: string | null | undefined, options: Intl.NumberFormatOptions) => {
  const tag = toIntlLocale(locale);
  const key = `${tag}|${JSON.stringify(options)}`;
  let formatter = formatterCache.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(tag, { useGrouping: "always", ...options });
    formatterCache.set(key, formatter);
  }
  return formatter;
};

// CLDR shortens Catalan thousands to "k" and thousands of millions to "kM", forms Catalan readers
// rarely meet; the press writes "mil" and "mil M", as Spanish does.
const COMPACT_WORDS: Record<string, Record<string, string>> = {
  "ca-ES": { k: "mil", kM: "mil M" },
};

const formatCompact = (
  locale: string | null | undefined,
  options: Intl.NumberFormatOptions,
  value: number
) => {
  const formatter = getFormatter(locale, { ...options, notation: "compact" });
  const words = COMPACT_WORDS[toIntlLocale(locale)];
  if (!words) return formatter.format(value);
  const parts = formatter.formatToParts(value);
  return parts
    .map((part, index) => {
      const word = part.type === "compact" ? words[part.value] : undefined;
      if (!word) return part.value;
      // Some engines attach the symbol to "k" ("k€"); a word needs the space back.
      return parts[index + 1]?.type === "currency" ? `${word}\u00a0` : word;
    })
    .join("");
};

const decimalOptions = (minimumFractionDigits = 2, maximumFractionDigits = 2) => {
  const minimum = Math.max(2, minimumFractionDigits ?? 2);
  return { minimumFractionDigits: minimum, maximumFractionDigits: Math.max(minimum, maximumFractionDigits ?? 2) };
};

const normalizeCurrencyCode = (currency?: string | null): string => {
  return normalizeQuoteCurrencyCode(currency) ?? "USD";
};

const currencySupport = new Map<string, boolean>();
const isSupportedCurrency = (currency: string) => {
  let supported = currencySupport.get(currency);
  if (supported === undefined) {
    try {
      new Intl.NumberFormat("en-US", { style: "currency", currency });
      supported = true;
    } catch {
      supported = false;
    }
    currencySupport.set(currency, supported);
  }
  return supported;
};

// The dollar is the app's own display currency, so the bare "$" is unambiguous; other dollars
// keep their prefix (CA$, A$) so a mixed portfolio stays readable.
const currencyOptions = (currency?: string | null): Intl.NumberFormatOptions => {
  const normalized = normalizeCurrencyCode(currency);
  const code = isSupportedCurrency(normalized) ? normalized : "USD";
  return {
    style: "currency",
    currency: code,
    currencyDisplay: code === "USD" ? "narrowSymbol" : "symbol",
  };
};

const signDisplay = (includeSign: boolean): Intl.NumberFormatOptions["signDisplay"] =>
  includeSign ? "exceptZero" : "auto";

export const getCurrencySymbol = (currency?: string | null, locale?: string | null): string => {
  if (isPriceSubunitCurrency(currency)) {
    return normalizeCurrencyCode(currency);
  }
  return (
    getFormatter(locale, { ...currencyOptions(currency), minimumFractionDigits: 0, maximumFractionDigits: 0 })
      .formatToParts(0)
      .find((part) => part.type === "currency")?.value ?? "$"
  );
};

/** Whether the locale writes the currency symbol after the amount ("12 €") rather than before ("€12"). */
export const isCurrencySymbolAfterAmount = (currency?: string | null, locale?: string | null): boolean => {
  if (isPriceSubunitCurrency(currency)) return true;
  const parts = getFormatter(locale, { ...currencyOptions(currency), minimumFractionDigits: 0, maximumFractionDigits: 0 })
    .formatToParts(1);
  return parts.findIndex((part) => part.type === "currency") > parts.findIndex((part) => part.type === "integer");
};

const toNumeric = (input: PrimitiveValue): number | null => {
  if (input === null || input === undefined) {
    return null;
  }

  if (typeof input === "number") {
    return Number.isFinite(input) ? input : null;
  }

  if (typeof input === "string") {
    const trimmed = input.trim();
    if (!trimmed) {
      return null;
    }
    const parsed = Number(trimmed.replace(/,/g, ""));
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

// Pence and similar price units have no symbol of their own, so the code follows the amount.
const formatSubunitAmount = (
  numeric: number,
  currency: string,
  { includeSign = false, locale, compact = false }: { includeSign?: boolean; locale?: string | null; compact?: boolean }
) => {
  const options = { ...decimalOptions(), signDisplay: signDisplay(includeSign) };
  const amount = compact
    ? formatCompact(locale, options, numeric)
    : getFormatter(locale, options).format(numeric);
  return `${amount} ${currency}`;
};

export const formatCurrencyWithCommas = (
  value: PrimitiveValue,
  { includeSign = false, currency = "USD", locale }: CurrencyFormatOptions = {}
): string => {
  const numeric = toNumeric(value);
  if (numeric === null) {
    return "—";
  }

  const normalizedCurrency = normalizeCurrencyCode(currency);
  if (isPriceSubunitCurrency(normalizedCurrency)) {
    return formatSubunitAmount(numeric, normalizedCurrency, { includeSign, locale });
  }
  return getFormatter(locale, {
    ...currencyOptions(normalizedCurrency),
    ...decimalOptions(),
    signDisplay: signDisplay(includeSign),
  }).format(numeric);
};

/**
 * Whole units, for amounts a reader skims rather than reconciles: "412 €", not "412,00 €".
 * Cents belong on statements and positions, not in a headline.
 */
export const formatWholeCurrencyWithCommas = (
  value: PrimitiveValue,
  { currency = "USD", locale }: Omit<CurrencyFormatOptions, "includeSign" | "maximumFractionDigits"> = {}
): string => {
  const numeric = toNumeric(value);
  if (numeric === null) {
    return "—";
  }

  const normalizedCurrency = normalizeCurrencyCode(currency);
  const digits = { minimumFractionDigits: 0, maximumFractionDigits: 0 };
  if (isPriceSubunitCurrency(normalizedCurrency)) {
    return `${getFormatter(locale, digits).format(numeric)} ${normalizedCurrency}`;
  }
  return getFormatter(locale, { ...currencyOptions(normalizedCurrency), ...digits }).format(numeric);
};

/** Thousands and above use the locale's own short forms: "€2.35M", "2,35 M€", "2,35 Mio. €". */
export const formatAbbreviatedCurrency = (
  value: PrimitiveValue,
  { maximumFractionDigits = 2, currency = "USD", locale }: CurrencyFormatOptions = {}
): string => {
  const numeric = toNumeric(value);
  if (numeric === null) {
    return "—";
  }

  if (Math.abs(numeric) < 1_000) {
    return formatCurrencyWithCommas(numeric, { currency, locale });
  }

  const normalizedCurrency = normalizeCurrencyCode(currency);
  if (isPriceSubunitCurrency(normalizedCurrency)) {
    return formatSubunitAmount(numeric, normalizedCurrency, { locale, compact: true });
  }
  return formatCompact(locale, {
    ...currencyOptions(normalizedCurrency),
    ...decimalOptions(0, maximumFractionDigits),
  }, numeric);
};

/**
 * Money on a chart axis: the locale's short forms with only the decimals a tick needs
 * ("1,5 M€", "500 mil €", "€1.5M"), and whole units below a thousand ("0 €").
 */
export const formatCompactWholeCurrency = (
  value: PrimitiveValue,
  { maximumFractionDigits = 1, currency = "USD", locale }: CurrencyFormatOptions = {}
): string => {
  const numeric = toNumeric(value);
  if (numeric === null) {
    return "—";
  }

  if (Math.abs(numeric) < 1_000) {
    return formatWholeCurrencyWithCommas(numeric, { currency, locale });
  }

  const normalizedCurrency = normalizeCurrencyCode(currency);
  const digits = { minimumFractionDigits: 0, maximumFractionDigits };
  if (isPriceSubunitCurrency(normalizedCurrency)) {
    return `${formatCompact(locale, digits, numeric)} ${normalizedCurrency}`;
  }
  return formatCompact(locale, { ...currencyOptions(normalizedCurrency), ...digits }, numeric);
};

/** Millions and above use the locale's own short forms ("2.35M", "2,35 Mio."); smaller numbers stay whole. */
export const formatAbbreviatedNumber = (
  value: PrimitiveValue,
  { maximumFractionDigits = 2, locale }: { maximumFractionDigits?: number } & LocaleOption = {}
): string => {
  const numeric = toNumeric(value);
  if (numeric === null) {
    return "—";
  }

  const options = decimalOptions(0, maximumFractionDigits);
  return Math.abs(numeric) >= 1_000_000
    ? formatCompact(locale, options, numeric)
    : getFormatter(locale, options).format(numeric);
};

/** Short forms from the thousands up ("12.5K", "12,5 mil"), with the decimals the caller asks for. */
export const formatCompactNumber = (
  value: PrimitiveValue,
  {
    minimumFractionDigits = 0,
    maximumFractionDigits = 2,
    locale,
  }: { minimumFractionDigits?: number; maximumFractionDigits?: number } & LocaleOption = {}
): string => {
  const numeric = toNumeric(value);
  if (numeric === null) {
    return "—";
  }
  return formatCompact(locale, { minimumFractionDigits, maximumFractionDigits }, numeric);
};

/** The sign follows the locale's number, and "%" sits right after it in every language. */
export const formatPercentWithCommas = (
  value: PrimitiveValue,
  { includeSign = false, locale }: { includeSign?: boolean } & LocaleOption = {}
): string => {
  const numeric = toNumeric(value);
  if (numeric === null) {
    return "—";
  }

  const formatted = getFormatter(locale, {
    ...decimalOptions(),
    signDisplay: signDisplay(includeSign),
  }).format(numeric);
  return `${formatted}%`;
};

/** A percentage without decimals, for figures that are read at a glance: "64%". */
export const formatWholePercentWithCommas = (
  value: PrimitiveValue,
  { locale }: LocaleOption = {}
): string => {
  const numeric = toNumeric(value);
  if (numeric === null) {
    return "—";
  }

  return `${getFormatter(locale, { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(numeric)}%`;
};

/**
 * A share in a list or a comparison, always with one decimal so a column lines up: "63,4%",
 * "5,0%". Anything above zero but under 0.05 reads "<0,1%" rather than a misleading "0,0%".
 */
export const formatPercentOneDecimal = (
  value: PrimitiveValue,
  { locale }: LocaleOption = {}
): string => {
  const numeric = toNumeric(value);
  if (numeric === null) {
    return "—";
  }
  const formatter = getFormatter(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  return numeric > 0 && numeric < 0.05 ? `<${formatter.format(0.1)}%` : `${formatter.format(numeric)}%`;
};

/** Exactly this many decimals, for figures that sit side by side: "4,8 punts". */
export const formatFixedNumber = (
  value: PrimitiveValue,
  digits: number,
  { locale }: LocaleOption = {}
): string => {
  const numeric = toNumeric(value);
  if (numeric === null) {
    return "—";
  }
  return getFormatter(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(numeric);
};

/** Up to this many decimals and no trailing zeros: "74", "36,3". */
export const formatNumberUpTo = (
  value: PrimitiveValue,
  maximumDigits: number,
  { locale }: LocaleOption = {}
): string => {
  const numeric = toNumeric(value);
  if (numeric === null) {
    return "—";
  }
  return getFormatter(locale, { minimumFractionDigits: 0, maximumFractionDigits: maximumDigits }).format(numeric);
};

/** A count, written the reader's way: "1.480". */
export const formatWholeNumberWithCommas = (
  value: PrimitiveValue,
  { locale }: LocaleOption = {}
): string => {
  const numeric = toNumeric(value);
  if (numeric === null) {
    return "—";
  }

  return getFormatter(locale, { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(numeric);
};

export const formatSignedPercentWithCommas = (
  value: PrimitiveValue,
  options: LocaleOption = {}
): string => formatPercentWithCommas(value, { ...options, includeSign: true });

export const formatSignedCurrencyWithCommas = (
  value: PrimitiveValue,
  options: Omit<CurrencyFormatOptions, "includeSign"> = {}
): string => formatCurrencyWithCommas(value, { ...options, includeSign: true });

export const formatNumberWithCommas = (
  value: PrimitiveValue,
  {
    minimumFractionDigits = 0,
    maximumFractionDigits = 2,
    locale,
  }: { minimumFractionDigits?: number; maximumFractionDigits?: number } & LocaleOption = {}
): string => {
  const numeric = toNumeric(value);
  if (numeric === null) {
    return "—";
  }

  return getFormatter(locale, decimalOptions(minimumFractionDigits, maximumFractionDigits)).format(numeric);
};

export const parseNumericString = (value: PrimitiveValue): number | null =>
  toNumeric(value);

/** The formatters above with the reader's locale already applied. */
export const createNumberFormat = (locale?: string | null) => ({
  locale: toIntlLocale(locale),
  formatNumberWithCommas: (
    value: PrimitiveValue,
    options: { minimumFractionDigits?: number; maximumFractionDigits?: number } = {}
  ) => formatNumberWithCommas(value, { ...options, locale }),
  formatPercentWithCommas: (value: PrimitiveValue, options: { includeSign?: boolean } = {}) =>
    formatPercentWithCommas(value, { ...options, locale }),
  formatSignedPercentWithCommas: (value: PrimitiveValue) =>
    formatSignedPercentWithCommas(value, { locale }),
  formatPercentOneDecimal: (value: PrimitiveValue) => formatPercentOneDecimal(value, { locale }),
  formatWholeNumberWithCommas: (value: PrimitiveValue) => formatWholeNumberWithCommas(value, { locale }),
  formatFixedNumber: (value: PrimitiveValue, digits: number) => formatFixedNumber(value, digits, { locale }),
  formatNumberUpTo: (value: PrimitiveValue, maximumDigits: number) => formatNumberUpTo(value, maximumDigits, { locale }),
  formatCurrencyWithCommas: (value: PrimitiveValue, options: Omit<CurrencyFormatOptions, "locale"> = {}) =>
    formatCurrencyWithCommas(value, { ...options, locale }),
  formatSignedCurrencyWithCommas: (
    value: PrimitiveValue,
    options: Omit<CurrencyFormatOptions, "locale" | "includeSign"> = {}
  ) => formatSignedCurrencyWithCommas(value, { ...options, locale }),
  formatAbbreviatedCurrency: (value: PrimitiveValue, options: Omit<CurrencyFormatOptions, "locale"> = {}) =>
    formatAbbreviatedCurrency(value, { ...options, locale }),
  formatAbbreviatedNumber: (value: PrimitiveValue, options: { maximumFractionDigits?: number } = {}) =>
    formatAbbreviatedNumber(value, { ...options, locale }),
  getCurrencySymbol: (currency?: string | null) => getCurrencySymbol(currency, locale),
  isCurrencySymbolAfterAmount: (currency?: string | null) => isCurrencySymbolAfterAmount(currency, locale),
});

export type NumberFormat = ReturnType<typeof createNumberFormat>;
